import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { DragDropContext } from "react-beautiful-dnd";
import generatePDF from 'react-to-pdf';
import Template1 from '../../templates/Template1';
import Template2 from '../../templates/Template2';
import Template3 from '../../templates/Template3';
import DynamicTemplate from '../../templates/DynamicTemplate';
import PrintWrapper from './PrintWrapper';
import { useStore } from "../../store";
import { dynamicTemplateIds, getDynamicTemplateConfig } from "../../templates/templateCatalog";

const Download = () => {
  const navigate = useNavigate();
  const { data, skills, selectedTemplate } = useStore();
  const boxRef = useRef(null);
  const [isPrintMode, setIsPrintMode] = useState(false);

  // Define margins for each template
  const templateMargins = {
    Template1: { top: 10, right: 10, bottom: 20, left: 10 },
    Template2: { top: 20, right: 15, bottom: 25, left: 15 },
    Template3: { top: 10, right: 8, bottom: 15, left: 8 },
  };

  const handleGeneratePDF = async () => {
    setIsPrintMode(true);

    // Force desktop viewport
    const viewport = document.createElement('meta');
    viewport.setAttribute('name', 'viewport');
    viewport.setAttribute('content', 'width=1024');
    document.head.appendChild(viewport);

    await new Promise((resolve) => setTimeout(resolve, 1500)); 

    // Get margin based on the selected template
    const selectedMargins = templateMargins[selectedTemplate] || templateMargins.Template1;

    const options = {
      filename: 'resume.pdf',
      page: {
        margin: selectedMargins,
        format: [210, 297], // A4 dimensions in mm
      },
      resolution: 3, // Increased resolution for better quality
      method: 'save',
      overrides: {
        pdf: {
          format: [210, 297],
          unit: 'mm',
        },
        canvas: {
          scale: 3,
          useCORS: true,
        },
      },
    };

    try {
      await generatePDF(boxRef, options);
    } catch (error) {
      console.error('PDF generation failed:', error);
    }

    // Cleanup viewport meta tag
    document.head.removeChild(viewport);
    setIsPrintMode(false);
  };

  const getTemplateNumber = () => {
    switch (selectedTemplate) {
      case 'Template1': return "1";
      case 'Template2': return "2";
      case 'Template3': return "3";
      default: return "1";
    }
  };

  const renderTemplate = () => {
    if (selectedTemplate === 'Template1') {
      return <Template1 data={data} skills={skills} downloadable={true} style={{ width: '210mm', minHeight: '297mm' }} />;
    }

    if (selectedTemplate === 'Template2') {
      return <Template2 data={data} skills={skills} downloadable={true} style={{ width: '210mm', minHeight: '297mm' }} />;
    }

    if (selectedTemplate === 'Template3') {
      return <Template3 data={data} skills={skills} downloadable={true} style={{ width: '210mm', minHeight: '297mm' }} />;
    }

    if (dynamicTemplateIds.has(selectedTemplate)) {
      return (
        <DynamicTemplate
          data={data}
          skills={skills}
          downloadable={true}
          config={getDynamicTemplateConfig(selectedTemplate)}
        />
      );
    }

    return <Template1 data={data} skills={skills} downloadable={true} style={{ width: '210mm', minHeight: '297mm' }} />;
  };

  useEffect(() => {
    handleGeneratePDF();

    const handleAfterPrint = () => {
      navigate("/");
    };

    window.addEventListener("afterprint", handleAfterPrint);
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [navigate]);

  return (
    <Box
      ref={boxRef}
      sx={{
        width: '210mm',
        minHeight: '297mm',
        margin: '0 auto',
        bgcolor: 'white',
        position: 'relative',
        overflow: 'hidden',

        // Force desktop layout
        '@media screen': {
          width: '210mm !important',
          minHeight: '297mm !important',
          padding: '0 !important',
          margin: '0 auto !important',
          transform: 'none !important',
          maxWidth: 'none !important',
        },

        // Print mode styles
        ...(isPrintMode && {
          width: '210mm !important',
          minHeight: '297mm !important',
          padding: '0 !important',
          margin: '0 !important',
          transform: 'none !important',
          maxWidth: 'none !important',
        }),

        // Ensure child elements maintain desktop layout
        '& *': {
          maxWidth: 'none !important',
          flexShrink: '0 !important',
          transform: 'none !important',
        }
      }}
    >
      <DragDropContext>
        <PrintWrapper templateType={getTemplateNumber()}>
          {renderTemplate()}
        </PrintWrapper>
      </DragDropContext>
    </Box>
  );
};

export default Download;
